import React, { useState, useEffect } from 'react';
import { 
  Search, Lock, Unlock, Save, X, Check, Edit2, 
  Crown, AlertCircle, Filter, ChevronLeft, ChevronRight,
  Gift, Clock, Link, ExternalLink, Copy, RefreshCw,
  FileText, Eye, EyeOff
} from 'lucide-react';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/firestore';

const TrainingAccessManager = () => {
  const [trainings, setTrainings] = useState([]);
  const [courses, setCourses] = useState([]);
  const [filteredTrainings, setFilteredTrainings] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [activeTab, setActiveTab] = useState('trainings');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [copySuccess, setCopySuccess] = useState(null);
  
  // Модальное окно для подарков
  const [giftModal, setGiftModal] = useState({
    isOpen: false,
    item: null,
    giftData: {
      isGift: true,
      duration: 7,
      hasTimer: true,
      timerEndDate: null
    }
  });

  // Модальное окно для Stripe ссылки
  const [stripeModal, setStripeModal] = useState({
    isOpen: false,
    item: null,
    linkValue: ''
  });

  // Статистика
  const [stats, setStats] = useState({
    totalTrainings: 0,
    premiumTrainings: 0,
    paidTrainings: 0,
    publishedTrainings: 0,
    draftTrainings: 0,
    giftTrainings: 0,
    withStripeLinkTrainings: 0,
    totalCourses: 0,
    premiumCourses: 0,
    paidCourses: 0,
    publishedCourses: 0,
    draftCourses: 0,
    giftCourses: 0,
    withStripeLinkCourses: 0
  });

  // Хранилище существующих ID для проверки уникальности
  const [existingPaidIds, setExistingPaidIds] = useState(new Set());

  // Загрузка тренингов и курсов
  useEffect(() => {
    loadData();
  }, []);

  // Собираем все существующие ID для проверки уникальности
  useEffect(() => {
    const ids = new Set();
    
    trainings.forEach(t => {
      if (t.paidAccessId) ids.add(t.paidAccessId);
    });
    
    courses.forEach(c => {
      if (c.paidAccessId) ids.add(c.paidAccessId);
    });
    
    setExistingPaidIds(ids);
  }, [trainings, courses]);

  // Фильтрация и поиск для тренингов
  useEffect(() => {
    let filtered = [...trainings];
    
    if (searchTerm) {
      filtered = filtered.filter(t => 
        t.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    switch(filterType) {
      case 'premium':
        filtered = filtered.filter(t => t.premium === true);
        break;
      case 'paid':
        filtered = filtered.filter(t => t.paidAccessId && t.paidAccessId.trim() !== '');
        break;
      case 'published':
        filtered = filtered.filter(t => t.status === 'published' || (!t.status && t.status !== 'draft'));
        break;
      case 'draft':
        filtered = filtered.filter(t => t.status === 'draft');
        break;
      case 'gift':
        filtered = filtered.filter(t => t.isGift === true);
        break;
      case 'hasStripeLink':
        filtered = filtered.filter(t => t.stripePaymentLink && t.stripePaymentLink.trim() !== '');
        break;
      default:
        break;
    }
    
    setFilteredTrainings(filtered);
    setCurrentPage(1);
  }, [searchTerm, filterType, trainings]);

  // Фильтрация и поиск для курсов
  useEffect(() => {
    let filtered = [...courses];
    
    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    switch(filterType) {
      case 'premium':
        filtered = filtered.filter(c => c.premium === true);
        break;
      case 'paid':
        filtered = filtered.filter(c => c.paidAccessId && c.paidAccessId.trim() !== '');
        break;
      case 'published':
        filtered = filtered.filter(c => c.status === 'published' || (!c.status && c.status !== 'draft'));
        break;
      case 'draft':
        filtered = filtered.filter(c => c.status === 'draft');
        break;
      case 'gift':
        filtered = filtered.filter(c => c.isGift === true);
        break;
      case 'hasStripeLink':
        filtered = filtered.filter(c => c.stripePaymentLink && c.stripePaymentLink.trim() !== '');
        break;
      default:
        break;
    }
    
    setFilteredCourses(filtered);
    setCurrentPage(1);
  }, [searchTerm, filterType, courses]);

  // Обновление статистики
  useEffect(() => {
    // Статистика по тренингам
    const totalTrainings = trainings.length;
    const premiumTrainings = trainings.filter(t => t.premium === true).length;
    const paidTrainings = trainings.filter(t => t.paidAccessId && t.paidAccessId.trim() !== '').length;
    const giftTrainings = trainings.filter(t => t.isGift === true).length;
    const withStripeLinkTrainings = trainings.filter(t => t.stripePaymentLink && t.stripePaymentLink.trim() !== '').length;
    const draftTrainings = trainings.filter(t => t.status === 'draft').length;
    const publishedTrainings = trainings.filter(t => t.status !== 'draft').length;
    
    // Статистика по курсам
    const totalCourses = courses.length;
    const premiumCourses = courses.filter(c => c.premium === true).length;
    const paidCourses = courses.filter(c => c.paidAccessId && c.paidAccessId.trim() !== '').length;
    const giftCourses = courses.filter(c => c.isGift === true).length;
    const withStripeLinkCourses = courses.filter(c => c.stripePaymentLink && c.stripePaymentLink.trim() !== '').length;
    const draftCourses = courses.filter(c => c.status === 'draft').length;
    const publishedCourses = courses.filter(c => c.status !== 'draft').length;
    
    setStats({ 
      totalTrainings, premiumTrainings, paidTrainings, publishedTrainings, draftTrainings, giftTrainings, withStripeLinkTrainings,
      totalCourses, premiumCourses, paidCourses, publishedCourses, draftCourses, giftCourses, withStripeLinkCourses
    });
  }, [trainings, courses]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Загрузка тренингов
      const trainingsQuerySnapshot = await getDocs(collection(db, "trainings"));
      const trainingsData = trainingsQuerySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      trainingsData.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dateB - dateA;
      });
      
      setTrainings(trainingsData);
      setFilteredTrainings(trainingsData);

      // Загрузка курсов
      const coursesQuerySnapshot = await getDocs(collection(db, "courses"));
      const coursesData = coursesQuerySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      coursesData.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dateB - dateA;
      });
      
      setCourses(coursesData);
      setFilteredCourses(coursesData);
    } catch (error) {
      console.error("Error loading data:", error);
      alert('Ошибка при загрузке данных');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Генерирует уникальный числовой ID (3-4 цифры)
   */
  const generateNumericPaidAccessId = () => {
    const min = 100;
    const max = 9999;
    const randomNum = Math.floor(Math.random() * (max - min + 1)) + min;
    return randomNum.toString();
  };

  /**
   * Генерирует уникальный ID, проверяя его наличие в существующих
   */
  const generateUniquePaidAccessId = () => {
    let attempts = 0;
    const maxAttempts = 50;
    
    while (attempts < maxAttempts) {
      const newId = generateNumericPaidAccessId();
      
      if (!existingPaidIds.has(newId)) {
        return newId;
      }
      
      attempts++;
    }
    
    const timestamp = Date.now().toString().slice(-4);
    return timestamp;
  };

  const handleEdit = (item, field) => {
    setEditingId(item.id);
    setEditingField(field);
    setEditValue(item[field] || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingField(null);
    setEditValue('');
  };

  const handleSave = async (itemId, collectionName) => {
    setSavingId(itemId);
    try {
      const itemRef = doc(db, collectionName, itemId);
      const updateData = {
        [editingField]: editValue.trim() || null,
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(itemRef, updateData);
      
      if (collectionName === "trainings") {
        setTrainings(prev => prev.map(t => 
          t.id === itemId 
            ? { ...t, [editingField]: editValue.trim() || null }
            : t
        ));
      } else {
        setCourses(prev => prev.map(c => 
          c.id === itemId 
            ? { ...c, [editingField]: editValue.trim() || null }
            : c
        ));
      }
      
      setEditingId(null);
      setEditingField(null);
      setEditValue('');
    } catch (error) {
      console.error("Error updating item:", error);
      alert('Ошибка при сохранении');
    } finally {
      setSavingId(null);
    }
  };

  /**
   * Генерирует и сохраняет числовой ID платного доступа
   */
  const handleGenerateAndSavePaidAccessId = async (item, collectionName) => {
    if (!item) return;
    
    setSavingId(item.id);
    try {
      const generatedId = generateUniquePaidAccessId();
      
      const itemRef = doc(db, collectionName, item.id);
      await updateDoc(itemRef, {
        paidAccessId: generatedId,
        updatedAt: new Date().toISOString()
      });
      
      if (collectionName === "trainings") {
        setTrainings(prev => prev.map(t => 
          t.id === item.id ? { ...t, paidAccessId: generatedId } : t
        ));
      } else {
        setCourses(prev => prev.map(c => 
          c.id === item.id ? { ...c, paidAccessId: generatedId } : c
        ));
      }
      
      setExistingPaidIds(prev => new Set(prev).add(generatedId));
      
      navigator.clipboard.writeText(generatedId);
      setCopySuccess(`generated-${item.id}`);
      setTimeout(() => setCopySuccess(null), 2000);
      
    } catch (error) {
      console.error("Error generating paid access ID:", error);
      alert('Ошибка при генерации ID');
    } finally {
      setSavingId(null);
    }
  };

  const handleClearField = async (itemId, collectionName, field) => {
    if (!window.confirm(`Очистить ${field === 'paidAccessId' ? 'ID платного доступа' : 'ссылку Stripe'}?`)) return;
    
    setSavingId(itemId);
    try {
      const itemRef = doc(db, collectionName, itemId);
      await updateDoc(itemRef, {
        [field]: null,
        updatedAt: new Date().toISOString()
      });
      
      if (collectionName === "trainings") {
        setTrainings(prev => prev.map(t => 
          t.id === itemId ? { ...t, [field]: null } : t
        ));
      } else {
        setCourses(prev => prev.map(c => 
          c.id === itemId ? { ...c, [field]: null } : c
        ));
      }
    } catch (error) {
      console.error("Error clearing field:", error);
      alert('Ошибка при очистке');
    } finally {
      setSavingId(null);
    }
  };

  const togglePremium = async (itemId, currentStatus, collectionName) => {
    setSavingId(itemId);
    try {
      const itemRef = doc(db, collectionName, itemId);
      await updateDoc(itemRef, {
        premium: !currentStatus,
        updatedAt: new Date().toISOString()
      });
      
      if (collectionName === "trainings") {
        setTrainings(prev => prev.map(t => 
          t.id === itemId 
            ? { ...t, premium: !currentStatus }
            : t
        ));
      } else {
        setCourses(prev => prev.map(c => 
          c.id === itemId 
            ? { ...c, premium: !currentStatus }
            : c
        ));
      }
    } catch (error) {
      console.error("Error toggling premium:", error);
      alert('Ошибка при изменении статуса');
    } finally {
      setSavingId(null);
    }
  };

  // Публикация (делает видимым для пользователей с соответствующим доступом)
  const publishItem = async (itemId, collectionName) => {
    if (!window.confirm('Опубликовать? Материал станет видимым для пользователей (доступ определяется настройками Premium/ID/Подарок).')) return;
    
    setSavingId(itemId);
    try {
      const itemRef = doc(db, collectionName, itemId);
      await updateDoc(itemRef, {
        status: 'published',
        updatedAt: new Date().toISOString()
      });
      
      if (collectionName === "trainings") {
        setTrainings(prev => prev.map(t => 
          t.id === itemId 
            ? { ...t, status: 'published' }
            : t
        ));
      } else {
        setCourses(prev => prev.map(c => 
          c.id === itemId 
            ? { ...c, status: 'published' }
            : c
        ));
      }
    } catch (error) {
      console.error("Error publishing item:", error);
      alert('Ошибка при публикации');
    } finally {
      setSavingId(null);
    }
  };

  // Перевод в черновик (скрывает от всех)
  const setDraftStatus = async (itemId, collectionName) => {
    if (!window.confirm('Перевести в черновик? Это скроет материал от всех пользователей.')) return;
    
    setSavingId(itemId);
    try {
      const itemRef = doc(db, collectionName, itemId);
      await updateDoc(itemRef, {
        status: 'draft',
        updatedAt: new Date().toISOString()
      });
      
      if (collectionName === "trainings") {
        setTrainings(prev => prev.map(t => 
          t.id === itemId 
            ? { ...t, status: 'draft' }
            : t
        ));
      } else {
        setCourses(prev => prev.map(c => 
          c.id === itemId 
            ? { ...c, status: 'draft' }
            : c
        ));
      }
    } catch (error) {
      console.error("Error setting draft status:", error);
      alert('Ошибка при изменении статуса');
    } finally {
      setSavingId(null);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(id);
    setTimeout(() => setCopySuccess(null), 2000);
  };

  const openGiftModal = (item) => {
    setGiftModal({
      isOpen: true,
      item,
      giftData: {
        isGift: true,
        duration: item.giftDuration || 7,
        hasTimer: item.giftTimer || false,
        timerEndDate: item.giftTimerEndDate || null
      }
    });
  };

  const closeGiftModal = () => {
    setGiftModal({
      isOpen: false,
      item: null,
      giftData: {
        isGift: true,
        duration: 7,
        hasTimer: true,
        timerEndDate: null
      }
    });
  };

  const saveGiftSettings = async () => {
    const { item, giftData } = giftModal;
    if (!item) return;

    setSavingId(item.id);
    try {
      const collectionName = activeTab === 'trainings' ? "trainings" : "courses";
      const itemRef = doc(db, collectionName, item.id);

      let timerEndDate = null;
      if (giftData.hasTimer) {
        timerEndDate = new Date();
        timerEndDate.setDate(timerEndDate.getDate() + giftData.duration);
      }

      await updateDoc(itemRef, {
        isGift: true,
        giftDuration: giftData.duration,
        giftTimer: giftData.hasTimer,
        giftTimerEndDate: timerEndDate ? timerEndDate.toISOString() : null,
        updatedAt: new Date().toISOString()
      });
      
      if (collectionName === "trainings") {
        setTrainings(prev => prev.map(t => 
          t.id === item.id 
            ? { 
                ...t, 
                isGift: true,
                giftDuration: giftData.duration,
                giftTimer: giftData.hasTimer,
                giftTimerEndDate: timerEndDate ? timerEndDate.toISOString() : null
              }
            : t
        ));
      } else {
        setCourses(prev => prev.map(c => 
          c.id === item.id 
            ? { 
                ...c, 
                isGift: true,
                giftDuration: giftData.duration,
                giftTimer: giftData.hasTimer,
                giftTimerEndDate: timerEndDate ? timerEndDate.toISOString() : null
              }
            : c
        ));
      }
      
      closeGiftModal();
    } catch (error) {
      console.error("Error saving gift settings:", error);
      alert('Ошибка при сохранении настроек подарка');
    } finally {
      setSavingId(null);
    }
  };

  const removeGiftStatus = async (itemId, collectionName) => {
    if (!window.confirm('Убрать статус подарка?')) return;
    
    setSavingId(itemId);
    try {
      const itemRef = doc(db, collectionName, itemId);
      await updateDoc(itemRef, {
        isGift: false,
        giftDuration: null,
        giftTimer: null,
        giftTimerEndDate: null,
        updatedAt: new Date().toISOString()
      });
      
      if (collectionName === "trainings") {
        setTrainings(prev => prev.map(t => 
          t.id === itemId 
            ? { ...t, isGift: false, giftDuration: null, giftTimer: null, giftTimerEndDate: null }
            : t
        ));
      } else {
        setCourses(prev => prev.map(c => 
          c.id === itemId 
            ? { ...c, isGift: false, giftDuration: null, giftTimer: null, giftTimerEndDate: null }
            : c
        ));
      }
    } catch (error) {
      console.error("Error removing gift status:", error);
      alert('Ошибка при удалении статуса подарка');
    } finally {
      setSavingId(null);
    }
  };

  const openStripeModal = (item) => {
    setStripeModal({
      isOpen: true,
      item,
      linkValue: item.stripePaymentLink || ''
    });
  };

  const closeStripeModal = () => {
    setStripeModal({
      isOpen: false,
      item: null,
      linkValue: ''
    });
  };

  const saveStripeLink = async () => {
    const { item, linkValue } = stripeModal;
    if (!item) return;

    setSavingId(item.id);
    try {
      const collectionName = activeTab === 'trainings' ? "trainings" : "courses";
      const itemRef = doc(db, collectionName, item.id);

      await updateDoc(itemRef, {
        stripePaymentLink: linkValue.trim() || null,
        updatedAt: new Date().toISOString()
      });
      
      if (collectionName === "trainings") {
        setTrainings(prev => prev.map(t => 
          t.id === item.id 
            ? { ...t, stripePaymentLink: linkValue.trim() || null }
            : t
        ));
      } else {
        setCourses(prev => prev.map(c => 
          c.id === item.id 
            ? { ...c, stripePaymentLink: linkValue.trim() || null }
            : c
        ));
      }
      
      closeStripeModal();
    } catch (error) {
      console.error("Error saving Stripe link:", error);
      alert('Ошибка при сохранении ссылки Stripe');
    } finally {
      setSavingId(null);
    }
  };

  const getAccessStatus = (item) => {
    // Проверяем статус "Черновик"
    if (item.status === 'draft') {
      return { 
        label: 'Черновик', 
        color: 'text-gray-400',
        bg: 'bg-gray-700/50',
        icon: <FileText className="w-3 h-3 mr-1" />
      };
    }
    
    // Опубликованные материалы - статус определяется настройками доступа
    if (item.isGift) {
      const timerInfo = item.giftTimer && item.giftTimerEndDate 
        ? ` (${item.giftDuration} дн.)` 
        : '';
      return { 
        label: `Подарок${timerInfo}`, 
        color: 'text-pink-400',
        bg: 'bg-pink-900/30',
        icon: <Gift className="w-3 h-3 mr-1" />
      };
    } else if (item.premium && item.paidAccessId) {
      return { 
        label: 'Премиум + ID', 
        color: 'text-purple-400',
        bg: 'bg-purple-900/30',
        icon: <Crown className="w-3 h-3 mr-1" />
      };
    } else if (item.premium) {
      return { 
        label: 'Премиум', 
        color: 'text-yellow-400',
        bg: 'bg-yellow-900/30',
        icon: <Crown className="w-3 h-3 mr-1" />
      };
    } else if (item.paidAccessId) {
      return { 
        label: 'По ID', 
        color: 'text-green-400',
        bg: 'bg-green-900/30',
        icon: <Lock className="w-3 h-3 mr-1" />
      };
    } else {
      return { 
        label: 'Опубликован', 
        color: 'text-blue-400',
        bg: 'bg-blue-900/30',
        icon: <Eye className="w-3 h-3 mr-1" />
      };
    }
  };

  // Пагинация
  const currentItems = activeTab === 'trainings' ? filteredTrainings : filteredCourses;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPageItems = currentItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(currentItems.length / itemsPerPage);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black p-6">
      <div className="max-w-7xl mx-auto">
        {/* Заголовок */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center">
            <Lock className="w-6 h-6 mr-2 text-blue-400" />
            Управление доступом и Stripe ссылками
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Настройка статуса (Черновик/Опубликован), премиум статуса, ID платного доступа, подарков и ссылок Stripe
          </p>
        </div>

        {/* Вкладки */}
        <div className="flex gap-2 mb-4 border-b border-gray-800">
          <button
            onClick={() => {
              setActiveTab('trainings');
              setCurrentPage(1);
            }}
            className={`px-4 py-2 text-sm font-medium transition-colors relative ${
              activeTab === 'trainings'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Тренинги
            <span className="ml-2 px-2 py-0.5 text-xs bg-gray-800 rounded-full">
              {stats.totalTrainings}
            </span>
          </button>
          <button
            onClick={() => {
              setActiveTab('courses');
              setCurrentPage(1);
            }}
            className={`px-4 py-2 text-sm font-medium transition-colors relative ${
              activeTab === 'courses'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Курсы
            <span className="ml-2 px-2 py-0.5 text-xs bg-gray-800 rounded-full">
              {stats.totalCourses}
            </span>
          </button>
        </div>

        {/* Статистика */}
        <div className="grid grid-cols-7 gap-3 mb-4">
          <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
            <div className="text-xs text-gray-400">
              {activeTab === 'trainings' ? 'Всего тренингов' : 'Всего курсов'}
            </div>
            <div className="text-xl font-bold text-white">
              {activeTab === 'trainings' ? stats.totalTrainings : stats.totalCourses}
            </div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
            <div className="text-xs text-gray-400 flex items-center">
              <FileText className="w-3 h-3 mr-1 text-gray-400" /> Черновики
            </div>
            <div className="text-xl font-bold text-white">
              {activeTab === 'trainings' ? stats.draftTrainings : stats.draftCourses}
            </div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
            <div className="text-xs text-gray-400 flex items-center">
              <Eye className="w-3 h-3 mr-1 text-blue-400" /> Опубликовано
            </div>
            <div className="text-xl font-bold text-white">
              {activeTab === 'trainings' ? stats.publishedTrainings : stats.publishedCourses}
            </div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
            <div className="text-xs text-gray-400 flex items-center">
              <Crown className="w-3 h-3 mr-1 text-yellow-400" /> Премиум
            </div>
            <div className="text-xl font-bold text-white">
              {activeTab === 'trainings' ? stats.premiumTrainings : stats.premiumCourses}
            </div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
            <div className="text-xs text-gray-400 flex items-center">
              <Lock className="w-3 h-3 mr-1 text-green-400" /> По ID
            </div>
            <div className="text-xl font-bold text-white">
              {activeTab === 'trainings' ? stats.paidTrainings : stats.paidCourses}
            </div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
            <div className="text-xs text-gray-400 flex items-center">
              <Gift className="w-3 h-3 mr-1 text-pink-400" /> Подарки
            </div>
            <div className="text-xl font-bold text-white">
              {activeTab === 'trainings' ? stats.giftTrainings : stats.giftCourses}
            </div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
            <div className="text-xs text-gray-400 flex items-center">
              <Link className="w-3 h-3 mr-1 text-blue-400" /> Stripe
            </div>
            <div className="text-xl font-bold text-white">
              {activeTab === 'trainings' ? stats.withStripeLinkTrainings : stats.withStripeLinkCourses}
            </div>
          </div>
        </div>

        {/* Поиск и фильтры */}
        <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700 mb-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Поиск по названию..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-gray-900 text-white rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="bg-gray-900 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">Все</option>
                <option value="draft">Черновики</option>
                <option value="published">Опубликованные</option>
                <option value="premium">Только премиум</option>
                <option value="paid">С ID доступа</option>
                <option value="gift">Подарки</option>
                <option value="hasStripeLink">Со ссылкой Stripe</option>
              </select>
            </div>
            
            <button
              onClick={loadData}
              className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors text-sm"
            >
              Обновить
            </button>
          </div>
          
          <div className="mt-2 text-xs text-gray-400">
            Найдено: {currentItems.length} {activeTab === 'trainings' ? 'тренингов' : 'курсов'}
          </div>
        </div>

        {/* Список */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="bg-gray-800/30 rounded-lg border border-gray-700 overflow-hidden">
              {/* Заголовок таблицы */}
              <div className="grid grid-cols-12 gap-2 p-3 bg-gray-900/50 border-b border-gray-700 text-xs font-medium text-gray-400">
                <div className="col-span-4">Название</div>
                <div className="col-span-2">Статус</div>
                <div className="col-span-3">ID платного доступа</div>
                <div className="col-span-3">Ссылка Stripe</div>
              </div>

              {/* Список элементов */}
              <div className="divide-y divide-gray-700/50">
                {currentPageItems.map((item) => {
                  const status = getAccessStatus(item);
                  const isEditingPaidId = editingId === item.id && editingField === 'paidAccessId';
                  const isEditingStripeLink = editingId === item.id && editingField === 'stripePaymentLink';
                  const isSaving = savingId === item.id;
                  const collectionName = activeTab === 'trainings' ? "trainings" : "courses";
                  const isDraft = item.status === 'draft';
                  const isPublished = !isDraft;
                  const isPaidIdMissing = !item.paidAccessId || item.paidAccessId.trim() === '';
                  
                  return (
                    <div key={item.id} className={`grid grid-cols-12 gap-2 p-3 hover:bg-gray-800/30 transition-colors ${isDraft ? 'opacity-75' : ''}`}>
                      {/* Название */}
                      <div className="col-span-4 flex items-center">
                        <div className="w-8 h-8 rounded bg-gray-700 overflow-hidden flex-shrink-0 mr-2">
                          {item.image ? (
                            <img 
                              src={item.image} 
                              alt="" 
                              className="w-full h-full object-cover"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                              <span className="text-xs text-gray-500">
                                {activeTab === 'trainings' ? '📚' : '📖'}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="truncate">
                          <div className="text-sm font-medium text-white truncate flex items-center">
                            {isDraft && <EyeOff className="w-3 h-3 mr-1 text-gray-500" />}
                            {item.title || 'Без названия'}
                          </div>
                          <div className="text-xs text-gray-400">
                            {activeTab === 'trainings' 
                              ? `${item.lessons?.length || 0} уроков`
                              : `${item.blocks?.length || 0} блоков`
                            }
                          </div>
                        </div>
                      </div>

                      {/* Статус */}
                      <div className="col-span-2 flex items-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${status.bg} ${status.color}`}>
                          {status.icon}
                          {status.label}
                        </span>
                      </div>

                      {/* ID платного доступа */}
                      <div className="col-span-3 flex items-center">
                        {isEditingPaidId ? (
                          <div className="flex items-center w-full">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="flex-1 bg-gray-900 text-white rounded-l px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Введите ID"
                              autoFocus
                              disabled={isSaving}
                            />
                            <button
                              onClick={() => handleSave(item.id, collectionName)}
                              disabled={isSaving}
                              className="px-2 py-1 bg-green-600 text-white rounded-r hover:bg-green-700 disabled:opacity-50"
                              title="Сохранить"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={isSaving}
                              className="ml-1 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                              title="Отмена"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center w-full">
                            <span className={`text-sm truncate flex-1 ${item.paidAccessId ? 'text-green-400 font-mono' : 'text-gray-500 italic'}`}>
                              {item.paidAccessId || 'не указан'}
                            </span>
                            
                            {isPaidIdMissing && isPublished && (
                              <button
                                onClick={() => handleGenerateAndSavePaidAccessId(item, collectionName)}
                                disabled={isSaving}
                                className="ml-1 p-1 text-blue-400 hover:text-blue-300 bg-blue-500/10 rounded transition-colors"
                                title="Сгенерировать ID"
                              >
                                <RefreshCw className="w-3 h-3" />
                              </button>
                            )}
                            
                            {item.paidAccessId && (
                              <>
                                <button
                                  onClick={() => copyToClipboard(item.paidAccessId, `paid-${item.id}`)}
                                  className="ml-1 p-1 text-gray-400 hover:text-gray-300"
                                  title="Копировать ID"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                                {copySuccess === `paid-${item.id}` && (
                                  <span className="ml-1 text-xs text-green-400">✓</span>
                                )}
                                <button
                                  onClick={() => handleClearField(item.id, collectionName, 'paidAccessId')}
                                  disabled={isSaving}
                                  className="ml-1 p-1 text-red-400 hover:text-red-300 disabled:opacity-50"
                                  title="Очистить ID"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </>
                            )}
                            
                            {item.paidAccessId && (
                              <button
                                onClick={() => handleEdit(item, 'paidAccessId')}
                                disabled={isEditingPaidId || isSaving}
                                className="ml-1 p-1 text-blue-400 hover:text-blue-300 disabled:opacity-30"
                                title="Редактировать ID"
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Ссылка Stripe */}
                      <div className="col-span-3 flex items-center">
                        {isEditingStripeLink ? (
                          <div className="flex items-center w-full">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="flex-1 bg-gray-900 text-white rounded-l px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="https://buy.stripe.com/..."
                              autoFocus
                              disabled={isSaving}
                            />
                            <button
                              onClick={() => handleSave(item.id, collectionName)}
                              disabled={isSaving}
                              className="px-2 py-1 bg-green-600 text-white rounded-r hover:bg-green-700 disabled:opacity-50"
                              title="Сохранить"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={isSaving}
                              className="ml-1 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                              title="Отмена"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center w-full">
                            {item.stripePaymentLink ? (
                              <>
                                <span className="text-sm truncate flex-1 text-blue-400">
                                  {item.stripePaymentLink.length > 30 
                                    ? `${item.stripePaymentLink.substring(0, 30)}...` 
                                    : item.stripePaymentLink}
                                </span>
                                <a
                                  href={item.stripePaymentLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-1 p-1 text-blue-400 hover:text-blue-300"
                                  title="Открыть ссылку"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                                <button
                                  onClick={() => copyToClipboard(item.stripePaymentLink, `stripe-${item.id}`)}
                                  className="ml-1 p-1 text-gray-400 hover:text-gray-300"
                                  title="Копировать ссылку"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                                {copySuccess === `stripe-${item.id}` && (
                                  <span className="ml-1 text-xs text-green-400">✓</span>
                                )}
                                <button
                                  onClick={() => handleClearField(item.id, collectionName, 'stripePaymentLink')}
                                  disabled={isSaving}
                                  className="ml-1 p-1 text-red-400 hover:text-red-300 disabled:opacity-50"
                                  title="Очистить ссылку"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </>
                            ) : (
                              <span className="text-sm text-gray-500 italic flex-1">не указана</span>
                            )}
                            {isPublished && (
                              <button
                                onClick={() => handleEdit(item, 'stripePaymentLink')}
                                disabled={isEditingStripeLink || isSaving}
                                className="ml-1 p-1 text-blue-400 hover:text-blue-300 disabled:opacity-30"
                                title={item.stripePaymentLink ? "Редактировать ссылку" : "Добавить ссылку"}
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Действия */}
                      <div className="col-span-12 flex items-center justify-end gap-1 mt-2 pt-2 border-t border-gray-700/50">
                        {/* Кнопка подарок (для опубликованных, не черновиков) */}
                        {isPublished && !item.isGift && (
                          <button
                            onClick={() => openGiftModal(item)}
                            disabled={isSaving}
                            className="p-1.5 text-pink-400 hover:text-pink-300 bg-pink-500/10 rounded transition-colors"
                            title="Сделать подарком"
                          >
                            <Gift className="w-4 h-4" />
                          </button>
                        )}

                        {/* Если уже подарок - показываем кнопку управления */}
                        {item.isGift && isPublished && (
                          <>
                            <button
                              onClick={() => openGiftModal(item)}
                              disabled={isSaving}
                              className="p-1.5 text-pink-400 hover:text-pink-300 bg-pink-500/10 rounded transition-colors"
                              title="Настроить подарок"
                            >
                              <Gift className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => removeGiftStatus(item.id, collectionName)}
                              disabled={isSaving}
                              className="p-1.5 text-red-400 hover:text-red-300 bg-red-500/10 rounded transition-colors"
                              title="Убрать статус подарка"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}

                        {/* Кнопка премиум (только для опубликованных) */}
                        {isPublished && (
                          <button
                            onClick={() => togglePremium(item.id, item.premium, collectionName)}
                            disabled={isSaving}
                            className={`p-1.5 rounded transition-colors ${
                              item.premium 
                                ? 'text-yellow-400 hover:text-yellow-300 bg-yellow-500/10' 
                                : 'text-gray-400 hover:text-gray-300 bg-gray-700/30'
                            }`}
                            title={item.premium ? 'Убрать премиум' : 'Сделать премиум'}
                          >
                            <Crown className={`w-4 h-4 ${item.premium ? 'fill-yellow-400' : ''}`} />
                          </button>
                        )}

                        {/* Кнопка публикации (для черновиков) */}
                        {isDraft && (
                          <button
                            onClick={() => publishItem(item.id, collectionName)}
                            disabled={isSaving}
                            className="p-1.5 text-blue-400 hover:text-blue-300 bg-blue-500/10 rounded transition-colors"
                            title="Опубликовать"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}

                        {/* Кнопка черновик (для опубликованных) */}
                        {isPublished && (
                          <button
                            onClick={() => setDraftStatus(item.id, collectionName)}
                            disabled={isSaving}
                            className="p-1.5 text-gray-400 hover:text-gray-300 bg-gray-700/30 rounded transition-colors"
                            title="Перевести в черновик"
                          >
                            <EyeOff className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Пустой список */}
                {currentPageItems.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>{activeTab === 'trainings' ? 'Тренинги' : 'Курсы'} не найдены</p>
                  </div>
                )}
              </div>
            </div>

            {/* Пагинация */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-gray-400">
                  Показано {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, currentItems.length)} из {currentItems.length}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-3 py-1 bg-gray-800 text-white rounded">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Модальное окно для настройки подарка */}
      {giftModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white flex items-center">
                <Gift className="w-5 h-5 mr-2 text-pink-400" />
                Настройка подарка
              </h3>
              <button
                onClick={closeGiftModal}
                className="text-gray-400 hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-300 mb-1">Название:</p>
              <p className="text-white font-medium">{giftModal.item?.title}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Длительность доступа (дней)
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={giftModal.giftData.duration}
                  onChange={(e) => setGiftModal({
                    ...giftModal,
                    giftData: {
                      ...giftModal.giftData,
                      duration: parseInt(e.target.value) || 7
                    }
                  })}
                  className="w-full bg-gray-900 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-pink-500"
                />
              </div>

              <div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={giftModal.giftData.hasTimer}
                    onChange={(e) => setGiftModal({
                      ...giftModal,
                      giftData: {
                        ...giftModal.giftData,
                        hasTimer: e.target.checked
                      }
                    })}
                    className="form-checkbox h-4 w-4 text-pink-500 rounded bg-gray-900 border-gray-700"
                  />
                  <span className="text-sm text-gray-300 flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    Включить таймер обратного отсчета
                  </span>
                </label>
                {giftModal.giftData.hasTimer && (
                  <p className="text-xs text-gray-400 mt-1 ml-6">
                    Таймер начнется после активации подарка пользователем
                  </p>
                )}
              </div>

              {giftModal.giftData.hasTimer && (
                <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-700">
                  <p className="text-xs text-gray-400">
                    Доступ будет открыт на {giftModal.giftData.duration} дней
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={closeGiftModal}
                className="px-4 py-2 text-sm text-gray-400 hover:text-gray-300"
              >
                Отмена
              </button>
              <button
                onClick={saveGiftSettings}
                disabled={savingId === giftModal.item?.id}
                className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50 text-sm font-medium"
              >
                {savingId === giftModal.item?.id ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingAccessManager;